import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { BaseSignalRHub } from '@nimbus-global/features/signalr/baseSignalR.hub';
import { IUserSessionLive } from '@nimbus-global/features/reportingLiveData/models/user-session.model';
import {
  IServiceSessionLive,
  IServiceSessionLiveByDistributionProfile,
} from '@nimbus-global/features/reportingLiveData/models/service-session.model';
import { SIGNALR_DIAGNOSTIC_CLIENT } from '@nimbus-global/features/signalr/signalR.const';
import { IClientSideLogger } from '@nimbus-library/features/logger/services/client-logger/i-client-side-logger';
import { TwilioLiveMessage, TwilioMessageStatusUpdate } from '@nimbus-global/features/chat/models/chat-twilio.model';
import { CallRecordChangedNotification, CallRecordNotification } from '@nimbus-global/features/signalr/call-record-notification.model';
import { BulkEditOperationNotification } from '../bulk-edit/models/i-bulk-edit-operation-notification';
import { ForkRequestsService } from '../shared/fork-requests.service';
import { IUserTasksCapacity } from '../user-tasks-capacity/models/user-tasks-capacity.model';
import { IUserHeaderStateNotification } from './models/user-header-state-notification.model';
import { ITenantSettingsChangedNotification } from './models/tenant-settings-changed-notification.model';
import { ITaskFlowUpdatedNotification } from './models/task-flow-updated-notification.model';
import { IUserResponsibilityProfilesChangedNotification } from './models/user-responsibility-profiles-changed-notification.model';

@Injectable()
export abstract class INimbusSignalRHub extends BaseSignalRHub {
  public onBulkEditOperationNotification$: Subject<BulkEditOperationNotification> = new Subject<BulkEditOperationNotification>();
  public onConfigValuesChanged$: Subject<Record<string, string | null>> = new Subject<Record<string, string | null>>();
  public onFeaturesChanged$: Subject<IMap<boolean>> = new Subject<IMap<boolean>>();
  public onPermissionsChanged$: Subject<void> = new Subject();
  public onUserSessionsUpdated$: Subject<IUserSessionLive[]> = new Subject<IUserSessionLive[]>();
  public onServiceSessionsUpdated$: Subject<IServiceSessionLive[]> = new Subject<IServiceSessionLive[]>();
  public onServiceSessionsByDistributionProfileUpdated$: Subject<IServiceSessionLiveByDistributionProfile[]> = new Subject<
    IServiceSessionLiveByDistributionProfile[]
  >();
  public onCallRecordCreated$: Subject<CallRecordNotification> = new Subject<CallRecordNotification>();
  public onCallRecordChanged$: Subject<CallRecordChangedNotification> = new Subject<CallRecordChangedNotification>();
  public onChatHandlerMessageAdded$: Subject<TwilioLiveMessage> = new Subject<TwilioLiveMessage>();
  public onChatHandlerMessageStatusUpdated$: Subject<TwilioMessageStatusUpdate> = new Subject<TwilioMessageStatusUpdate>();
  public onUserCapacityChanged$: Subject<IUserTasksCapacity> = new Subject<IUserTasksCapacity>();
  public onUserTaskStateChanged$: Subject<IUserHeaderStateNotification> = new Subject<IUserHeaderStateNotification>();
  public onUserResponsibilityProfilesChanged$: Subject<IUserResponsibilityProfilesChangedNotification> =
    new Subject<IUserResponsibilityProfilesChangedNotification>();
  public onTenantSettingsChanged$: Subject<ITenantSettingsChangedNotification> = new Subject<ITenantSettingsChangedNotification>();
  public onTaskFlowUpdated$: Subject<ITaskFlowUpdatedNotification> = new Subject<ITaskFlowUpdatedNotification>();

  public constructor(
    protected readonly authenticationService: IAuthenticationService,
    protected readonly forkRequestService: ForkRequestsService,
    @Optional() @Inject(SIGNALR_DIAGNOSTIC_CLIENT) signalRDiagnosticClient: boolean,
    @Optional() clientSideLogger: IClientSideLogger,
  ) {
    super(forkRequestService, signalRDiagnosticClient, clientSideLogger);
  }

  protected abstract getIsReadyToConnect$(): Observable<boolean>;

  protected onConnected(): void {}

  protected onConnectedFailed(): void {}
}
